import React from "react";
import dayjs from "dayjs";
import { Trans } from "@lingui/macro";
import { useSelector } from "react-redux";
import { getColor } from "../../../../../utils";
import { Alert } from "../../../../../UI/Alert/Alert";
import { PopupDetailsSale } from "../popupDetailsSales";
import { Button } from "../../../../../UI/Button/Button";
import { ClipboardListIcon } from "@heroicons/react/outline";
import { LabelList } from "../../../../../UI/LabelList/LabelList";
import { ButtonLink } from "../../../../../UI/ButtonLink/ButtonLink";
import { IDetailSale } from "../../../../../../model/dashboardOrdersArchive/reducer";
import { getSaleByRefundId } from "../../../../../../model/dashboardRefundsArchive/api";
import { PopupPanelDashboard } from "../../../DashboardContentTable/PopupPanelDashboard";
import { numberCurrencyFormat } from "../../../../../../constants/convertToLocalCurrency";
import { selectRefundsArchiveDetailItem } from "../../../../../../model/dashboardRefundsArchive/selectors";

export type typePopupDetails = {
  variant?: string;
  crossLinkToSale?: boolean;
  setOpenPopup: React.Dispatch<React.SetStateAction<boolean>>;
};

export const PopupDetails: React.FC<typePopupDetails> = ({
  variant,
  setOpenPopup,
  crossLinkToSale,
}) => {
  const color = getColor(variant);

  const [openSale, setOpenSale] = React.useState<boolean>(false);
  const [sale, setSale] = React.useState<IDetailSale | undefined>();

  const dataObject = useSelector(selectRefundsArchiveDetailItem);

  if (!dataObject) {
    return <Alert type="error" title={<Trans>Have no data.</Trans>} />;
  }

  const onSaleClick = async () => {
    setOpenSale(true);
    getSaleByRefundId(dataObject.id).then((sale) => {
      if (sale.status === 200) {
        setSale(sale.data);
      } else {
        setSale(undefined);
        setOpenSale(true);
      }
    });
  };

  let vat: number = 0;

  if (dataObject) {
    if (dataObject!.productList.some((item) => item.discountAmount !== 0)) {
      vat = dataObject.productList.reduce((acc, curr) => {
        const discountPercent =
          curr.discountAmount > 0
            ? curr.discountAmount /
              (curr.customTotal ? curr.customTotal / curr.amount : curr.price)
            : 0;

        const currVat =
          curr.tax !== null
            ? curr.tax
            : curr.product?.vat
              ? curr.product.vat
              : 0;

        const currPrice =
          (curr.customTotal ? curr.customTotal / curr.amount : curr.price) *
          (1 - discountPercent);

        const vatForItem = (currVat * currPrice) / (1 + currVat);

        return acc + vatForItem * curr.amount;
      }, 0);
    } else {
      vat = dataObject.productList.reduce((acc, curr) => {
        const currVat =
          curr.tax !== null
            ? curr.tax
            : curr.product?.vat
              ? curr.product.vat
              : 0;

        // noinspection PointlessArithmeticExpressionJS
        const currPrice =
          (curr.customTotal ? curr.customTotal / curr.amount : curr.price) * 1;

        const vatForItem = (currVat * currPrice) / (1 + currVat);

        return acc + vatForItem * curr.amount;
      }, 0);
    }
  }

  return dataObject ? (
    <>
      <div className="grid gap-x-8 grid-cols-1 sm:grid-cols-2 sm:border sm:rounded-md sm:px-4">
        <div className="flex flex-wrap gap-x-4 py-2 border-t sm:border-t-0 border-b">
          <div className="text-gray-500 text-left">
            <Trans>Refunds Id:</Trans>
          </div>

          <div className="text-gray-900 text-left ">{dataObject.id}</div>
        </div>

        <div className="flex flex-wrap gap-x-4 py-2 border-b">
          <div className="text-gray-500 text-left">
            <Trans>Date</Trans>:
          </div>

          <div className="text-gray-900 text-left">
            {dataObject?.date
              ? dayjs(dataObject.date).format("YYYY-MM-DD HH:mm:ss")
              : "-"}
          </div>
        </div>

        <div className="flex flex-wrap gap-x-4 py-2 sm:py-4 border-b">
          <div className="flex gap-x-2 items-center text-gray-500 text-left">
            <Trans>Z-report number: </Trans>
          </div>

          <div className="text-gray-900 text-left">
            {dataObject.zreportNumber ?? "-"}
          </div>
        </div>

        <div className="flex flex-wrap gap-x-4 py-2 border-t-0 border-b">
          <div className="text-gray-500 text-left">
            <Trans>Refund amount:</Trans>
          </div>

          <div className="text-gray-900 text-left">
            {dataObject?.amount
              ? numberCurrencyFormat(+dataObject.amount)
              : "-"}
          </div>
        </div>

        <div className="flex flex-wrap gap-x-4 py-2 border-b sm:border-b-0">
          <div className="text-gray-500 text-left">
            <Trans>Receipt number</Trans>:
          </div>

          <div className="text-gray-900 text-left">
            {dataObject.receiptNumber ?? " - "}
          </div>
        </div>

        {crossLinkToSale && (
          <div className="flex flex-wrap gap-x-4 py-2 border-b sm:border-b-0 relative">
            <div className="text-gray-500 text-left">
              <Trans>Sale</Trans>:
            </div>

            <div className="text-gray-900 text-left flex-grow divide-y">
              <p className="flex justify-end">
                <ButtonLink
                  type="button"
                  onClick={onSaleClick}
                  label={<Trans>Sale info</Trans>}
                  buttonClassName="py-1 px-2 mt-2 self-end"
                />
              </p>
            </div>
          </div>
        )}
      </div>

      <div className="mt-5 sm:mt-2.5 grid gap-x-8 grid-cols-1 sm:grid-cols-2 sm:border sm:rounded-md sm:px-4">
        {process.env.REACT_APP_CUSTOMER !== "SKAPPSK" && (
          <div className="flex flex-wrap gap-x-4 py-2 border-t sm:border-b-0 sm:border-t-0">
            <div className="text-gray-500 text-left">
              <Trans>Fiscal module ID: </Trans>
            </div>

            <div className="text-gray-900 text-left">
              {dataObject.fmId ?? "-"}
            </div>
          </div>
        )}

        <div className="flex flex-wrap gap-x-4 py-2 border-b sm:border-0">
          <div className="flex gap-x-2 items-center text-gray-500 text-left">
            <Trans>Applet version</Trans>:
          </div>

          <div className="text-gray-900 text-left">
            {dataObject.appletVersion ?? "-"}
          </div>
        </div>
      </div>

      <div className="mt-5 sm:mt-2.5 grid gap-x-8 grid-cols-1 sm:grid-cols-2 sm:border sm:rounded-md sm:px-4">
        <div className="flex flex-wrap gap-x-4 py-2 border-b sm:border-b-0">
          <div className="flex gap-x-2 items-center text-gray-500 text-left">
            <Trans>User name: </Trans>
          </div>

          <div className="text-left text-gray-900">
            {dataObject.userName ?? "-"}
          </div>
        </div>

        <div className="flex flex-wrap gap-x-4 py-2 border-b sm:border-b-0">
          <div className="flex gap-x-2 items-center text-gray-500 text-left">
            <Trans>Store:</Trans>
          </div>

          <div className="text-left text-gray-900">
            {dataObject.store?.name ?? "-"}
          </div>
        </div>
      </div>

      <div className="mt-4 sm:mt-2.5 py-1.5 grid gap-x-8 grid-cols-1 sm:border sm:rounded-md sm:px-4 overflow-x-auto">
        <span className="flex gap-x-1 font-medium py-1 mb-1.5">
          <span className="text-gray-400">
            <ClipboardListIcon className="h-6 w-6" />
          </span>

          <Trans>Refund list</Trans>
        </span>

        <table className="min-w-full divide-y divide-gray-200 border-t">
          <thead className="bg-gray-50">
            <tr className="relative">
              <th
                scope="col"
                className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
              >
                <Trans>Product ID</Trans>
              </th>
              <th
                scope="col"
                className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
              >
                <Trans>Name</Trans>
              </th>
              <th
                scope="col"
                className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
              >
                <Trans>Label</Trans>
              </th>
              <th
                scope="col"
                className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
              >
                <Trans>Count</Trans>
              </th>
              <th
                scope="col"
                className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
              >
                <Trans>Price for unit</Trans>
              </th>
              {process.env.REACT_APP_CUSTOMER !== "SKAPPSK" && (
                <th
                  scope="col"
                  className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                >
                  <Trans>VAT</Trans>
                </th>
              )}
              <th
                scope="col"
                className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
              >
                <Trans>Discount</Trans>
              </th>
              <th
                scope="col"
                className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
              >
                <Trans>Total price</Trans>
              </th>
            </tr>
          </thead>

          <tbody className="divide-y">
            {dataObject?.productList?.length ? (
              dataObject.productList.map((item, index) => {
                let tax;
                if ("tax" in item && item.tax !== null) {
                  tax = item.tax;
                } else if (item.product && "vat" in item.product) {
                  tax = item.product.vat;
                } else {
                  tax = 0;
                }
                return (
                  <tr
                    key={index}
                    className={` bg-white hover:bg-${color}-50  align-top`}
                  >
                    <td className="px-3 py-2 whitespace-nowrap text-xs text-gray-500">
                      {item.id}
                    </td>

                    <td className="px-3 py-2 whitespace-nowrap text-sm text-gray-900">
                      {item.productName || item?.product?.name}
                    </td>

                    <td className="px-3 py-2 whitespace-nowrap text-sm text-gray-500">
                      <LabelList labelsList={item.markedLabels || []} />
                    </td>

                    <td className="px-3 py-2 whitespace-nowrap text-sm text-gray-500">
                      {item.amount ?? " - "}
                    </td>

                    <td className="px-3 py-2 whitespace-nowrap text-sm text-gray-500">
                      {item?.price ? numberCurrencyFormat(item.price) : " - "}
                    </td>

                    {process.env.REACT_APP_CUSTOMER !== "SKAPPSK" && (
                      <td className="px-3 py-2 whitespace-nowrap text-sm text-gray-500">
                        {(tax * 100).toLocaleString(undefined, {
                          maximumFractionDigits: 2,
                        })}
                        %
                      </td>
                    )}

                    <td className="px-3 py-2 whitespace-nowrap text-sm text-gray-500">
                      {numberCurrencyFormat(+item.discountAmount.toFixed(2))}
                    </td>

                    <td className="px-3 py-2 whitespace-nowrap text-sm text-gray-500">
                      {numberCurrencyFormat(
                        +(
                          item.amount * item.price -
                          (item.discountAmount || 0)
                        ).toFixed(2),
                      )}
                    </td>
                  </tr>
                );
              })
            ) : (
              <tr>
                <td className="px-3 py-2 whitespace-nowrap text-sm text-gray-500 col-span-9">
                  <Trans>Have no data.</Trans>
                </td>
              </tr>
            )}

            {process.env.REACT_APP_CUSTOMER !== "SKAPPSK" && (
              <tr>
                <td
                  colSpan={7}
                  className="px-3 py-2 whitespace-nowrap text-xs text-gray-500 col-span-4 text-left"
                >
                  <Trans>VAT including</Trans>:
                </td>
                <td className="px-3 py-2 whitespace-nowrap text-sm text-gray-500">
                  {numberCurrencyFormat(vat)}
                </td>
              </tr>
            )}
            <tr>
              <td
                colSpan={process.env.REACT_APP_CUSTOMER === "SKAPPSK" ? 6 : 7}
                className="px-3 py-2 whitespace-nowrap text-xs text-gray-900 col-span-4 text-left"
              >
                <Trans>Total</Trans>:
              </td>
              <td className="px-3 py-2 whitespace-nowrap text-sm text-gray-900">
                {dataObject?.amount
                  ? numberCurrencyFormat(+dataObject.amount)
                  : "-"}
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <div className="flex gap-4 mt-5 sm:mt-4 sm:flex sm:flex-row-reverse">
        <Button
          type="button"
          secondary={true}
          label={<Trans>Close</Trans>}
          onClick={() => setOpenPopup(false)}
        />
      </div>

      <PopupPanelDashboard
        openPopup={openSale}
        setOpenPopup={setOpenSale}
        title={<Trans>Fool sale information</Trans>}
      >
        <PopupDetailsSale sale={sale} setOpenPopup={setOpenSale} />
      </PopupPanelDashboard>
    </>
  ) : (
    <Alert type="error" title={<Trans>Have no data.</Trans>} />
  );
};
